import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';
import HitComponent from '../../Hit';
import { Fragment, useEffect, FC } from 'react';
import { AlgoliaCustomSort, ISearchProduct } from '@monofront/types';
import { useRouter } from 'next/router';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import Link from 'next/link';
import { useMediaQuery, useMerchCard } from '@monofront/business/hooks';
import { useTranslations } from 'next-intl';
import { useCacheFirstAuthentication } from '@context/persistedAuthContext';
import Pagination from '@monofront/design-system/components/Pagination';
import { useRecommendationTracking } from '@hooks/tracking/useRecommendationTracking';
import { usePagination } from './usePagination';

interface PaginatedHitsProps {
  customSort?: AlgoliaCustomSort<ISearchProduct>;
}

const PaginatedHits: FC<PaginatedHitsProps> = ({ customSort }) => {
  const { results } = useInstantSearch();
  const { sendEvent } = useInfiniteHits<ISearchProduct>();
  const merchCard = useMerchCard(useCacheFirstAuthentication);
  const isMobileWebsite = useMediaQuery(800);
  const router = useRouter();
  const products = customSort ? customSort(results.hits) : results.hits;
  const tCatalog = useTranslations('Catalog');

  const pagination = usePagination({
    onPageChange: () => {
      window.scrollTo(0, 0);
    },
  });

  const currentMaximumProductsQuantity =
    results.hitsPerPage * pagination.currentPage + results.hits.length;

  const viewedProducts = results.hitsPerPage * pagination.currentPage + 1;

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const scrollY = sessionStorage.getItem('searchScrollY');

    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY, 10));
      sessionStorage.removeItem('searchScrollY');
    }

    return () => {
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  const { sendSelectItemEvent } = useRecommendationTracking({
    trackingId: router.asPath,
  });

  const trackProductInteraction = (product: any, index: number) => {
    sendSelectItemEvent({
      product: { sku: product.sku, name: product.title, index },
    });
  };

  return (
    <>
      <div className="tw-mb-3 tw-grid tw-grid-cols-1 tw-gap-3 min-[350px]:tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-px-4 desktop:tw-px-0 lg:tw-grid-cols-4 xl:tw-grid-cols-5">
        {products.map((product, index) => {
          return (
            <Fragment key={product.objectID}>
              <HitComponent
                hit={product}
                sendEvent={sendEvent}
                trackProductInteraction={() =>
                  trackProductInteraction(product, index)
                }
              />
              {index === (isMobileWebsite ? 2 : 3) && merchCard && (
                <Link
                  href={merchCard.targetLink}
                  data-testid={`InfiniteHits-go-to-${index + 1}`}
                  className="tw-text-center"
                >
                  <ResponsiveImage
                    src={merchCard.imageUrl}
                    alt={merchCard.imageAlt}
                    width={isMobileWebsite ? 172 : 234}
                    height={isMobileWebsite ? 339 : 408}
                    UNSAFE_className="tw-rounded-lg"
                  />
                </Link>
              )}
            </Fragment>
          );
        })}
      </div>
      <Pagination
        message={tCatalog('seenProducts', {
          viewedProducts,
          currentIndexLimit: currentMaximumProductsQuantity,
          totalProducts: results.nbHits,
        })}
        {...pagination}
      />
    </>
  );
};

export default PaginatedHits;
